<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { formatDate24h } from '@/utils/dateFormatter'
import { useToast } from '@/composables/useToast'

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'manage',
    subject: 'WrapupGroupStatus',
  },
})

const { t } = useI18n()
const { showToast } = useToast()

// 👉 Data
const isDialogVisible = ref(false)
const isEditDialogVisible = ref(false)
const currentWrapupGroupStatus = ref<any>(null)
const saving = ref(false)

// 👉 Table headers
const headers = computed(() => [
  {
    title: t('wrapup.name'),
    key: 'name',
    sortable: true,
  },
  {
    title: t('wrapup.description'),
    key: 'description',
    sortable: true,
  },
  {
    title: t('wrapup.color'),
    key: 'color',
    sortable: false,
  },
  {
    title: t('wrapup.icon'),
    key: 'icon',
    sortable: false,
  },
  {
    title: t('wrapup.showInCallWork'),
    key: 'showInCallWork',
    sortable: true,
    align: 'center',
  },
  {
    title: t('wrapup.sortOrder'),
    key: 'sortOrder',
    sortable: true,
  },
  {
    title: t('common.isActive'),
    key: 'isActive',
    sortable: true,
  },
  {
    title: t('common.createdAt'),
    key: 'createdAt',
    sortable: true,
  },
  {
    title: t('common.actions'),
    key: 'actions',
    sortable: false,
  },
])

// 👉 Form data
const wrapupGroupStatusForm = ref({
  name: '',
  description: '',
  color: '',
  icon: '',
  isActive: true,
  showInCallWork: true,
  sortOrder: 0,
})

// 👉 Search and filter options
const searchQuery = ref('')
const selectedIsActive = ref()

// IsActive options
const isActiveOptions = computed(() => [
  { title: t('common.active'), value: true },
  { title: t('common.inactive'), value: false },
])

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

// Update data table options
const updateOptions = (options: any) => {
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// 👉 Fetching wrapup group statuses
const { data: wrapupGroupStatusesData, execute: loadWrapupGroupStatuses, isFetching: loading } = useApi<any>(createUrl('/wrapup-group-status', {
  query: {
    itemsPerPage,
    page,
    q: searchQuery,
    isActive: selectedIsActive,
    sortBy,
    orderBy,
  },
}))

const wrapupGroupStatuses = computed(() => wrapupGroupStatusesData.value?.data || [])
const totalItems = computed(() => wrapupGroupStatusesData.value?.total || 0)

// 👉 Open add dialog
const openAddDialog = () => {
  isDialogVisible.value = true
  wrapupGroupStatusForm.value = {
    name: '',
    description: '',
    color: '',
    icon: '',
    isActive: true,
    showInCallWork: true,
    sortOrder: 0,
  }
}

// 👉 Open edit dialog
const openEditDialog = (wrapupGroupStatus: any) => {
  currentWrapupGroupStatus.value = wrapupGroupStatus
  wrapupGroupStatusForm.value = {
    name: wrapupGroupStatus.name || '',
    description: wrapupGroupStatus.description || '',
    color: wrapupGroupStatus.color || '',
    icon: wrapupGroupStatus.icon || '',
    isActive: wrapupGroupStatus.isActive !== undefined ? wrapupGroupStatus.isActive : true,
    showInCallWork: wrapupGroupStatus.showInCallWork !== undefined ? wrapupGroupStatus.showInCallWork : true,
    sortOrder: wrapupGroupStatus.sortOrder || 0,
  }
  isEditDialogVisible.value = true
}

// 👉 Close dialogs
const closeAddDialog = () => {
  isDialogVisible.value = false
  wrapupGroupStatusForm.value = {
    name: '',
    description: '',
    color: '',
    icon: '',
    isActive: true,
    showInCallWork: true,
    sortOrder: 0,
  }
}

const closeEditDialog = () => {
  isEditDialogVisible.value = false
  currentWrapupGroupStatus.value = null
  wrapupGroupStatusForm.value = {
    name: '',
    description: '',
    color: '',
    icon: '',
    isActive: true,
    sortOrder: 0,
  }
}

// 👉 Save wrapup group status
const saveWrapupGroupStatus = async () => {
  try {
    saving.value = true

    if (currentWrapupGroupStatus.value) {
      // Update existing wrapup group status
      await $api(`/wrapup-group-status/${currentWrapupGroupStatus.value.id}`, {
        method: 'PATCH',
        body: wrapupGroupStatusForm.value,
      })
      showToast({
        type: 'success',
        message: t('wrapup.wrapupGroupStatusUpdatedSuccessfully'),
      })
    }
    else {
      // Create new wrapup group status
      await $api('/wrapup-group-status', {
        method: 'POST',
        body: wrapupGroupStatusForm.value,
      })
      showToast({
        type: 'success',
        message: t('wrapup.wrapupGroupStatusCreatedSuccessfully'),
      })
    }

    await loadWrapupGroupStatuses()
    closeAddDialog()
    closeEditDialog()
  }
  catch (error) {
    console.error('Failed to save wrapup group status:', error)
    showToast({
      type: 'error',
      message: t('wrapup.error'),
    })
  }
  finally {
    saving.value = false
  }
}

// 👉 Toggle active status
const toggleActiveStatus = async (wrapupGroupStatus: any) => {
  // Prevent toggling system status
  if (wrapupGroupStatus.isSystem) {
    showToast({
      type: 'error',
      message: t('wrapup.systemStatusCannotBeModified'),
    })

    return
  }

  try {
    await $api(`/wrapup-group-status/${wrapupGroupStatus.id}/toggle-active`, {
      method: 'PATCH',
    })
    showToast({
      type: 'success',
      message: t('wrapup.wrapupGroupStatusStatusToggled'),
    })
    await loadWrapupGroupStatuses()
  }
  catch (error) {
    console.error('Failed to toggle active status:', error)
    showToast({
      type: 'error',
      message: t('wrapup.error'),
    })
  }
}

// 👉 Load data on mount
onMounted(() => {
  loadWrapupGroupStatuses()
})
</script>

<template>
  <section>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('wrapup.wrapupGroupStatuses') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('wrapup.manageWrapupGroupStatuses') }}
      </p>
    </div>

    <VCard class="mb-6">
      <VCardItem class="pb-4">
        <VCardTitle>{{ t('common.filters') }}</VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <!-- 👉 Select IsActive -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="selectedIsActive"
              :placeholder="t('common.selectStatus')"
              :items="isActiveOptions"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardText class="d-flex flex-wrap gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: t('common.all') },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 15.625rem;">
            <AppTextField
              v-model="searchQuery"
              :placeholder="t('wrapup.searchWrapupGroupStatuses')"
            />
          </div>

          <!-- 👉 Add wrapup group status button -->
          <VBtn
            prepend-icon="tabler-plus"
            @click="openAddDialog"
          >
            {{ t('common.add') }} {{ t('wrapup.wrapupGroupStatus') }}
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- SECTION datatable -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="wrapupGroupStatuses"
        :items-length="totalItems"
        :headers="headers"
        :loading="loading"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <!-- 👉 Name -->
        <template #item.name="{ item }">
          <div class="d-flex align-center gap-x-4">
            <VAvatar
              :color="(item as any).color || 'primary'"
              variant="tonal"
              size="34"
            >
              <VIcon :icon="(item as any).icon || 'tabler-circle'" />
            </VAvatar>
            <div class="d-flex flex-column">
              <div class="d-flex align-center gap-2">
                <h6 class="text-base">
                  {{ (item as any).name || '' }}
                </h6>
                <VChip
                  v-if="(item as any).isSystem"
                  size="x-small"
                  color="primary"
                  variant="tonal"
                >
                  {{ t('common.system') }}
                </VChip>
              </div>
            </div>
          </div>
        </template>

        <!-- 👉 Description -->
        <template #item.description="{ item }">
          <span class="text-sm">
            {{ (item as any).description || t('common.noDescription') }}
          </span>
        </template>

        <!-- 👉 Color -->
        <template #item.color="{ item }">
          <div class="d-flex align-center gap-2">
            <div
              v-if="(item as any).color"
              class="rounded"
              :style="{
                width: '20px',
                height: '20px',
                backgroundColor: (item as any).color,
                border: '1px solid #e0e0e0',
              }"
            />
            <span class="text-sm">
              {{ (item as any).color || t('common.noColor') }}
            </span>
          </div>
        </template>

        <!-- 👉 Icon -->
        <template #item.icon="{ item }">
          <div class="d-flex align-center gap-2">
            <VIcon
              v-if="(item as any).icon"
              :icon="(item as any).icon"
              size="20"
            />
            <span class="text-sm">
              {{ (item as any).icon || t('common.noIcon') }}
            </span>
          </div>
        </template>

        <!-- 👉 Sort Order -->
        <template #item.sortOrder="{ item }">
          <span class="text-sm">
            {{ (item as any).sortOrder || 0 }}
          </span>
        </template>

        <!-- 👉 Show In Call Work -->
        <template #item.showInCallWork="{ item }">
          <div class="d-flex justify-center">
            <VIcon
              :icon="(item as any).showInCallWork ? 'tabler-eye' : 'tabler-eye-off'"
              :color="(item as any).showInCallWork ? 'success' : 'error'"
              size="20"
            />
          </div>
        </template>

        <!-- 👉 Is Active -->
        <template #item.isActive="{ item }">
          <VChip
            :color="(item as any).isActive ? 'success' : 'error'"
            size="small"
            variant="tonal"
          >
            {{ (item as any).isActive ? t('common.active') : t('common.inactive') }}
          </VChip>
        </template>

        <!-- 👉 Created At -->
        <template #item.createdAt="{ item }">
          <span class="text-sm">
            {{ formatDate24h((item as any).createdAt) }}
          </span>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <template v-if="(item as any).isSystem">
            <!-- System status - show locked icon -->
            <VTooltip location="top">
              <template #activator="{ props }">
                <IconBtn
                  v-bind="props"
                  disabled
                >
                  <VIcon icon="tabler-lock" />
                </IconBtn>
              </template>
              <span>{{ t('wrapup.systemStatusCannotBeModified') }}</span>
            </VTooltip>
          </template>

          <template v-else>
            <!-- Regular status - show edit and toggle buttons -->
            <IconBtn @click="openEditDialog(item)">
              <VIcon icon="tabler-pencil" />
            </IconBtn>

            <IconBtn @click="toggleActiveStatus(item)">
              <VIcon :icon="(item as any).isActive ? 'tabler-eye-off' : 'tabler-eye'" />
            </IconBtn>
          </template>
        </template>

        <!-- pagination -->
        <template #bottom>
          <TablePagination
            v-model:page="page"
            :items-per-page="itemsPerPage"
            :total-items="totalItems"
          />
        </template>
      </VDataTableServer>
    <!-- SECTION -->
    </VCard>

    <!-- 👉 Add Dialog -->
    <VDialog
      v-model="isDialogVisible"
      max-width="600"
    >
      <VCard>
        <VCardTitle>
          {{ t('common.add') }} {{ t('wrapup.wrapupGroupStatus') }}
        </VCardTitle>

        <VCardText>
          <VForm @submit.prevent="saveWrapupGroupStatus">
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="wrapupGroupStatusForm.name"
                  :label="t('wrapup.name')"
                  :placeholder="t('wrapup.enterName')"
                  required
                />
              </VCol>

              <VCol cols="12">
                <AppTextarea
                  v-model="wrapupGroupStatusForm.description"
                  :label="t('wrapup.description')"
                  :placeholder="t('wrapup.enterDescription')"
                />
              </VCol>

              <VCol cols="12">
                <VColorPicker
                  v-model="wrapupGroupStatusForm.color"
                  :label="t('wrapup.color')"
                  eye-dropper-icon="tabler-color-picker"
                  mode="hex"
                  hide-inputs
                  size="large"
                />
              </VCol>

              <VCol cols="6">
                <AppTextField
                  v-model="wrapupGroupStatusForm.icon"
                  :label="t('wrapup.icon')"
                  :placeholder="t('wrapup.enterIcon')"
                />
              </VCol>

              <VCol cols="6">
                <AppTextField
                  v-model.number="wrapupGroupStatusForm.sortOrder"
                  :label="t('wrapup.sortOrder')"
                  type="number"
                  min="0"
                />
              </VCol>

              <VCol cols="6">
                <VSwitch
                  v-model="wrapupGroupStatusForm.isActive"
                  :label="t('common.isActive')"
                  color="success"
                  inset
                />
              </VCol>

              <VCol cols="6">
                <VSwitch
                  v-model="wrapupGroupStatusForm.showInCallWork"
                  :label="t('wrapup.showInCallWork')"
                  color="primary"
                  inset
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="closeAddDialog"
          >
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="saveWrapupGroupStatus"
          >
            {{ t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 👉 Edit Dialog -->
    <VDialog
      v-model="isEditDialogVisible"
      max-width="600"
    >
      <VCard>
        <VCardTitle>
          {{ t('common.edit') }} {{ t('wrapup.wrapupGroupStatus') }}
        </VCardTitle>

        <VCardText>
          <VForm @submit.prevent="saveWrapupGroupStatus">
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="wrapupGroupStatusForm.name"
                  :label="t('wrapup.name')"
                  :placeholder="t('wrapup.enterName')"
                  required
                />
              </VCol>

              <VCol cols="12">
                <AppTextarea
                  v-model="wrapupGroupStatusForm.description"
                  :label="t('wrapup.description')"
                  :placeholder="t('wrapup.enterDescription')"
                />
              </VCol>

              <VCol cols="12">
                <VColorPicker
                  v-model="wrapupGroupStatusForm.color"
                  :label="t('wrapup.color')"
                  eye-dropper-icon="tabler-color-picker"
                  mode="hex"
                  hide-inputs
                  size="large"
                />
              </VCol>

              <VCol cols="6">
                <AppTextField
                  v-model="wrapupGroupStatusForm.icon"
                  :label="t('wrapup.icon')"
                  :placeholder="t('wrapup.enterIcon')"
                />
              </VCol>

              <VCol cols="6">
                <AppTextField
                  v-model.number="wrapupGroupStatusForm.sortOrder"
                  :label="t('wrapup.sortOrder')"
                  type="number"
                  min="0"
                />
              </VCol>

              <VCol cols="6">
                <VSwitch
                  v-model="wrapupGroupStatusForm.isActive"
                  :label="t('common.isActive')"
                  color="success"
                  inset
                />
              </VCol>

              <VCol cols="6">
                <VSwitch
                  v-model="wrapupGroupStatusForm.showInCallWork"
                  :label="t('wrapup.showInCallWork')"
                  color="primary"
                  inset
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="closeEditDialog"
          >
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="saveWrapupGroupStatus"
          >
            {{ t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
